import type {Dispatch, RefObject, SetStateAction} from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {DesignSelection, PublishableUrlUpdatePayload, SocialMediaResizeAjaxParams} from '@Components/social-media/social-media.types';
import {PosterResizeRequiredStatus} from '@Components/social-media/social-media.types';
import {
  usePostId,
  useAccountNeedsResizeMap,
  useDesignsGraphicsMetaMapForAccountWithMediaType,
  useDesignsToPublish,
  usePublishableURLsForAccount,
  useWizardDesignsGraphicMetaMap,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {
  setAccountResizeStatusForMediaType,
  updateGraphicURLInGraphicsMetaForDesign,
  updatePublishableUrl,
  updatePublishingParamSocialPostCover,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {AccountResizeStatus, ResizeOptionsConfig} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import type {AccountResizeStatusForDesignPayload} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {getResizeConfigForGATracking} from '@Libraries/social-media-wizard-resize-library';
import {makeSocialMediaResizeAjaxCall, makeVideoPreviewGenerationAjaxCall} from '@Libraries/social-media-resize.library';
import {getSocialPlatformName} from '@Libraries/social-media-platform.library';
import {GA4EventName, GA4EventParamName, trackPublishingGA4Events} from '@Libraries/ga-events';
import {useAppDispatch} from '@/hooks';
import {useAccountsToPublishTo, usePublishingParams, useSocialMediaWizardPost} from '@Components/social-media/social-media.hooks';
import type {SocialPostCover} from '@Components/social-media/publish-params.types';
import {socialAccountPostCoverValidations} from '@Components/social-media-wizard/social-media-wizard.types';
import type {SocialMediaSelectCoverModalProps} from '@Components/modals/social-media-select-cover-modal';
import {openSelectCoverModal} from '@Components/modals/social-media-select-cover-modal';
import type {CropData, CropperPanelApplyResponse} from '@Panels/cropper-panel';
import type {SocialPostCoverParams} from '@Panels/social-media-select-cover-panel/social-media-select-cover-panel.types';
import {CoverSource} from '@Panels/social-media-select-cover-panel/social-media-select-cover-panel.types';
import {setIsLoading, setShowCoverGenerationError} from '@Panels/social-media-select-cover-panel/social-media-select-cover-panel-reducer';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {setPanelMessage, setShowLoading} from '@Panels/cropper-panel/cropper-panel-reducer';
import {PANEL_MESSAGE_TYPE} from '@Components/panel/panel.types';
import {getCacheBustedURL} from '@Utils/url.util';
import {generateCoverForPost} from '@Libraries/social-media-wizard-cover-library';

interface UsePublishParamsResizePipelineOpts {
  account: SocialAccount;
  platformMediaTypeRef: RefObject<PlatformMediaType>;
  platformMediaType: PlatformMediaType;
}

interface UsePublishParamsResizePipelineResult {
  isPreviewLoading: boolean;
  setIsPreviewLoading: Dispatch<SetStateAction<boolean>>;
  triggerDefaultResizing: () => void;
  updateResizingStatus: (accountResizeStatusToSet: AccountResizeStatus, design: DesignSelection, platformMediaType: PlatformMediaType) => void;
  updateGraphicUrlInGraphicsMetaForDesign: (graphicURL: string, design: DesignSelection, isVideo: boolean) => void;
}

export function usePublishParamsResizePipeline({
  account,
  platformMediaTypeRef,
  platformMediaType,
}: UsePublishParamsResizePipelineOpts): UsePublishParamsResizePipelineResult {
  const dispatch = useAppDispatch();
  const publishingParams = usePublishingParams();
  const wizardPost = useSocialMediaWizardPost();
  const accountNeedsResizingMapForDesigns = useAccountNeedsResizeMap(account.id);
  const designsGraphicsMetaMapForAccount = useDesignsGraphicsMetaMapForAccountWithMediaType(account.id, platformMediaTypeRef.current);
  const designsGraphicsMetaMap = useWizardDesignsGraphicMetaMap();
  const publishableUrls = usePublishableURLsForAccount();
  const processingResizeAjaxParamsRef = useRef<SocialMediaResizeAjaxParams[]>([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);

  const updateResizingStatus = (accountResizeStatusToSet: AccountResizeStatus, design: DesignSelection, platformMediaTypeArg: PlatformMediaType): void => {
    dispatch(
      setAccountResizeStatusForMediaType({
        idSocialMediaAccount: account.id,
        postResizeStatus: platformMediaTypeArg === PlatformMediaType.POST ? accountResizeStatusToSet : null,
        reelResizeStatus: platformMediaTypeArg === PlatformMediaType.REELS ? accountResizeStatusToSet : null,
        storyResizeStatus: platformMediaTypeArg === PlatformMediaType.STORIES ? accountResizeStatusToSet : null,
        idPoster: design.posterHashedId,
        idPage: design.pageHashedId,
      } as AccountResizeStatusForDesignPayload)
    );
  };

  const updateGraphicUrlInGraphicsMetaForDesign = useCallback((graphicURL: string, {posterHashedId, pageHashedId}: DesignSelection, isVideo: boolean): void => {
    dispatch(
      updateGraphicURLInGraphicsMetaForDesign({
        idSocialMediaAccount: account.id,
        designHashId: posterHashedId,
        pageHashId: pageHashedId,
        previewURL: graphicURL,
        isVideo,
        mediaType: platformMediaType,
      })
    );
  }, []);

  const trackGAEventForAutoResize = (resizeType: ResizeOptionsConfig | undefined): void => {
    if (resizeType) {
      trackPublishingGA4Events(GA4EventName.SOCIALWIZARD_AUTORESIZE, {
        [GA4EventParamName.TYPE]: getResizeConfigForGATracking(resizeType),
        [GA4EventParamName.PLATFORM]: getSocialPlatformName(account.type),
      });
    }
  };

  const getDefaultResizeAjaxParamsForPoster = (design: DesignSelection): SocialMediaResizeAjaxParams => {
    const socialMediaPlatform = account.type;
    const {posterHashedId, pageHashedId} = design;

    const ajaxResizeParams: SocialMediaResizeAjaxParams = {
      posterHashedId: posterHashedId,
      pageHashedId: pageHashedId,
      resizeType: ResizeOptionsConfig.FILL,
      idPost: wizardPost?.id ?? '',
      socialMediaPlatform,
      socialAccountId: account.id,
      isVideo: designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId].isVideo,
      platformMediaType: platformMediaTypeRef.current,
    };

    if (!publishingParams[account.id][platformMediaTypeRef.current]) {
      console.error('No publishing params found for account');
      return ajaxResizeParams;
    }

    return ajaxResizeParams;
  };

  const hasPosterBeenAlreadyDefaultResized = ({posterHashedId, pageHashedId}: DesignSelection): boolean => {
    if (!publishableUrls[account.id]) {
      console.error('No publishable URL found for account with ID: ', account.id);
      return false;
    }

    const publishableUrlForAccount = publishableUrls[account.id];
    const mediaType = platformMediaTypeRef.current;

    const publishableUrlsForPlatformMediaType = publishableUrlForAccount[mediaType];

    if (!publishableUrlsForPlatformMediaType) {
      console.error('No publishable URL found for account with ID: ', account.id, ' and platform media type: ', mediaType);
      return false;
    }

    const publishableUrlForDesign = publishableUrlsForPlatformMediaType[posterHashedId]?.[pageHashedId];

    if (!publishableUrlForDesign) {
      console.error('No publishable URL found for account with ID: ', account.id, ', platform media type: ', mediaType, ', design ID: ', posterHashedId, ' and : ', pageHashedId);
      return false;
    }

    const originalGraphicUrl = designsGraphicsMetaMap[posterHashedId]?.[pageHashedId]?.graphicURL;

    if (!accountNeedsResizingMapForDesigns) {
      return false;
    }

    const resizeRequiredForAccount = accountNeedsResizingMapForDesigns[posterHashedId]?.[pageHashedId];
    let needsResize: PosterResizeRequiredStatus | undefined = resizeRequiredForAccount?.needsResizing;

    if (platformMediaTypeRef.current === PlatformMediaType.STORIES) {
      needsResize = resizeRequiredForAccount.storyNeedsResizing;
    }

    if (platformMediaTypeRef.current === PlatformMediaType.REELS) {
      needsResize = resizeRequiredForAccount.reelNeedsResizing;
    }

    const isResizeNeeded =
      needsResize !== undefined && (needsResize === PosterResizeRequiredStatus.RESIZE_NEEDED || needsResize === PosterResizeRequiredStatus.BACKEND_OPTIMIZATION_NEEDED);

    if (!isResizeNeeded) {
      return true;
    }

    return isResizeNeeded && publishableUrlForDesign !== originalGraphicUrl;
  };

  const haveAllPosterBeenAlreadyDefaultResized = (): boolean => {
    if (!accountNeedsResizingMapForDesigns) {
      return true;
    }

    return Object.keys(accountNeedsResizingMapForDesigns).every((posterHashId) => {
      return Object.keys(accountNeedsResizingMapForDesigns[posterHashId]).every((pageHashId) =>
        hasPosterBeenAlreadyDefaultResized({posterHashedId: posterHashId, pageHashedId: pageHashId} as DesignSelection)
      );
    });
  };

  const getDefaultAjaxParamsForAllPosters = useCallback((): null | SocialMediaResizeAjaxParams[] => {
    if (!accountNeedsResizingMapForDesigns || !account || haveAllPosterBeenAlreadyDefaultResized()) {
      return null;
    }

    const resizeAjaxParamsForPosters: SocialMediaResizeAjaxParams[] = [];

    Object.keys(accountNeedsResizingMapForDesigns).forEach((posterHashId) => {
      Object.keys(accountNeedsResizingMapForDesigns[posterHashId]).forEach((pageHashId) => {
        const resizeInfoForDesign = accountNeedsResizingMapForDesigns[posterHashId]?.[pageHashId];
        if (resizeInfoForDesign && !hasPosterBeenAlreadyDefaultResized({posterHashedId: posterHashId, pageHashedId: pageHashId})) {
          const resizeAjaxParams = getDefaultResizeAjaxParamsForPoster({posterHashedId: posterHashId, pageHashedId: pageHashId});
          resizeAjaxParamsForPosters.push(resizeAjaxParams);
        }
      });
    });

    return resizeAjaxParamsForPosters;
  }, [accountNeedsResizingMapForDesigns]);

  const handlePreviewGenerationSuccess = (design: DesignSelection, s3FilePath: string, isVideo: boolean): void => {
    if (!s3FilePath) {
      updateResizingStatus(AccountResizeStatus.RESIZE_ERROR, design, platformMediaTypeRef.current);
      return;
    }

    updateGraphicUrlInGraphicsMetaForDesign(s3FilePath, design, isVideo);

    dispatch(
      updatePublishableUrl({
        accountId: account.id,
        posterHashId: design.posterHashedId,
        pageHashId: design.pageHashedId,
        platformMediaType: platformMediaTypeRef.current,
        graphicUrl: s3FilePath,
      } as PublishableUrlUpdatePayload)
    );

    updateResizingStatus(AccountResizeStatus.RESIZE_DONE, design, platformMediaTypeRef.current);
  };

  const handlePreviewGenerationError = (design: DesignSelection, originalPreviewUrl: string, isVideo: boolean): void => {
    updateResizingStatus(AccountResizeStatus.RESIZE_ERROR, design, platformMediaTypeRef.current);
    updateGraphicUrlInGraphicsMetaForDesign(originalPreviewUrl, design, isVideo);
  };

  const startPreviewGeneration = (design: DesignSelection, isVideo: boolean): void => {
    updateResizingStatus(AccountResizeStatus.RESIZING, design, platformMediaTypeRef.current);
    updateGraphicUrlInGraphicsMetaForDesign('', design, isVideo);
    setIsPreviewLoading(true);
  };

  const doStartDefaultResizingforPoster = (ajaxParamsForAccount: SocialMediaResizeAjaxParams): void => {
    const {posterHashedId, pageHashedId} = ajaxParamsForAccount;
    const designSelection: DesignSelection = {posterHashedId: posterHashedId, pageHashedId: pageHashedId};
    const {resizeType} = ajaxParamsForAccount;
    const originalPreviewUrl = designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId].graphicURL;
    const {isVideo} = designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId];

    startPreviewGeneration(designSelection, isVideo);
    trackGAEventForAutoResize(resizeType);

    makeSocialMediaResizeAjaxCall(ajaxParamsForAccount, isVideo)
      .then((s3FilePath: string) => {
        handlePreviewGenerationSuccess(designSelection, s3FilePath, isVideo);
      })
      .catch(() => {
        handlePreviewGenerationError(designSelection, originalPreviewUrl, isVideo);
        console.error(`Unable to resize design with poster id: ${posterHashedId} and page id: ${pageHashedId}`);
      });
  };

  const handleDefaultResize = (): void => {
    const ajaxParamsForAllPosters = getDefaultAjaxParamsForAllPosters();

    if (!ajaxParamsForAllPosters) {
      return;
    }

    processingResizeAjaxParamsRef.current = ajaxParamsForAllPosters;

    for (const params of ajaxParamsForAllPosters) {
      try {
        doStartDefaultResizingforPoster(params);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const triggerDefaultResizing = (): void => {
    if (processingResizeAjaxParamsRef.current.length !== 0) {
      return;
    }

    handleDefaultResize();
    processingResizeAjaxParamsRef.current = [];
  };

  const isDesignVideo = ({posterHashedId, pageHashedId}: DesignSelection): boolean => {
    return !!designsGraphicsMetaMapForAccount[posterHashedId]?.[pageHashedId]?.isVideo;
  };

  const getResizeStatusForDesign = ({posterHashedId, pageHashedId}: DesignSelection): PosterResizeRequiredStatus | undefined => {
    const resizeRequiredForAccount = accountNeedsResizingMapForDesigns?.[posterHashedId]?.[pageHashedId];
    if (!resizeRequiredForAccount) {
      return undefined;
    }

    if (platformMediaTypeRef.current === PlatformMediaType.STORIES) {
      return resizeRequiredForAccount.storyNeedsResizing;
    }

    if (platformMediaTypeRef.current === PlatformMediaType.REELS) {
      return resizeRequiredForAccount.reelNeedsResizing;
    }

    return resizeRequiredForAccount.needsResizing;
  };

  const doesDesignNeedResize = (design: DesignSelection): boolean => {
    const resizeStatus = getResizeStatusForDesign(design);
    return resizeStatus === PosterResizeRequiredStatus.RESIZE_NEEDED || resizeStatus === PosterResizeRequiredStatus.BACKEND_OPTIMIZATION_NEEDED;
  };

  const hasPreviewAlreadyBeenGenerated = ({posterHashedId, pageHashedId}: DesignSelection): boolean => {
    const publishableUrlForAccount = publishableUrls[account.id];
    if (!publishableUrlForAccount) {
      return false;
    }

    const publishableUrlsForMediaType = publishableUrlForAccount[platformMediaTypeRef.current];
    if (!publishableUrlsForMediaType) {
      return false;
    }

    const publishableUrlForDesign = publishableUrlsForMediaType[posterHashedId]?.[pageHashedId];
    const originalGraphicUrl = designsGraphicsMetaMap[posterHashedId]?.[pageHashedId]?.graphicURL;

    return !!publishableUrlForDesign && publishableUrlForDesign !== originalGraphicUrl;
  };

  const doesVideoNeedPreviewGeneration = (design: DesignSelection): boolean => {
    if (!isDesignVideo(design)) {
      return false;
    }

    if (doesDesignNeedResize(design)) {
      return false;
    }

    return !hasPreviewAlreadyBeenGenerated(design);
  };

  const doStartVideoPreviewGeneration = (design: DesignSelection): void => {
    const {posterHashedId, pageHashedId} = design;
    const originalPreviewUrl = designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId].graphicURL;
    const {isVideo} = designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId];

    startPreviewGeneration(design, isVideo);

    makeVideoPreviewGenerationAjaxCall(design)
      .then((s3FilePath: string) => {
        handlePreviewGenerationSuccess(design, s3FilePath, isVideo);
      })
      .catch(() => {
        handlePreviewGenerationError(design, originalPreviewUrl, isVideo);
        console.error(`Unable to generate video preview for poster id: ${posterHashedId} and page id: ${pageHashedId}`);
      });
  };

  const forEachDesign = (callback: (design: DesignSelection) => void): void => {
    if (!accountNeedsResizingMapForDesigns) {
      return;
    }

    Object.keys(accountNeedsResizingMapForDesigns).forEach((posterHashId) => {
      Object.keys(accountNeedsResizingMapForDesigns[posterHashId]).forEach((pageHashId) => {
        callback({posterHashedId: posterHashId, pageHashedId: pageHashId});
      });
    });
  };

  const triggerPreviewGenerationForUnresizedVideos = (): void => {
    forEachDesign((design) => {
      if (doesVideoNeedPreviewGeneration(design)) {
        try {
          doStartVideoPreviewGeneration(design);
        } catch (e) {
          console.error(e);
        }
      }
    });
  };

  const initializeVideoPreviews = (): void => {
    if (!accountNeedsResizingMapForDesigns || !account) {
      return;
    }

    triggerDefaultResizing();
    triggerPreviewGenerationForUnresizedVideos();
  };

  useEffect((): void => {
    initializeVideoPreviews();
  }, [accountNeedsResizingMapForDesigns]);

  return {
    isPreviewLoading,
    setIsPreviewLoading,
    triggerDefaultResizing,
    updateResizingStatus,
    updateGraphicUrlInGraphicsMetaForDesign,
  };
}

interface UsePublishParamsCoverFlowOpts {
  account: SocialAccount;
  platformMediaType: PlatformMediaType;
  platformMediaTypeRef: RefObject<PlatformMediaType>;
  isVideoDesign: boolean;
}

interface UsePublishParamsCoverFlowResult {
  onClickCover: () => void;
  getCoverUrlForSingleGraphicPost: string;
  isCoverCta: () => boolean;
  getTextForCoverCta: () => string;
}

export function usePublishParamsCoverFlow({
  account,
  platformMediaType,
  platformMediaTypeRef,
  isVideoDesign,
}: UsePublishParamsCoverFlowOpts): UsePublishParamsCoverFlowResult {
  const dispatch = useAppDispatch();
  const designsToPublishTo = useDesignsToPublish();
  const accountsToPublishTo = useAccountsToPublishTo();
  const designsGraphicsMetaMapForAccount = useDesignsGraphicsMetaMapForAccountWithMediaType(account.id, platformMediaTypeRef.current);
  const idPost = usePostId();

  const getCoverUrlForSingleGraphicPost = useMemo((): string => {
    if (designsToPublishTo.length !== 1) {
      return 'no-cover-url';
    }

    const {posterHashedId, pageHashedId} = designsToPublishTo[0];
    const graphicsMeta = designsGraphicsMetaMapForAccount[posterHashedId]?.[pageHashedId];

    if (!graphicsMeta?.cover) {
      return 'no-cover-url';
    }

    return graphicsMeta.cover.coverUrl;
  }, [designsToPublishTo, designsGraphicsMetaMapForAccount]);

  const shouldShowCommonCoverCheckBoxForCoverPanel = (): boolean => {
    const accountsSupportingPostCoverToPublishTo = accountsToPublishTo.filter((account) => socialAccountPostCoverValidations[account.type]?.doesSupportCover);

    return accountsSupportingPostCoverToPublishTo.length !== 1;
  };

  const getTextForCoverCta = (): string => {
    const {posterHashedId, pageHashedId} = designsToPublishTo[0];
    if (designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId].cover) {
      return window.i18next.t('pmwjs_edit_cover');
    }

    return window.i18next.t('pmwjs_add_cover');
  };

  const isCoverCta = (): boolean => {
    if (!isVideoDesign || platformMediaTypeRef.current === PlatformMediaType.STORIES) {
      return false;
    }
    return designsToPublishTo.length === 1 && socialAccountPostCoverValidations[account.type].doesSupportCover;
  };

  const getTimestampForPostCover = (): number => {
    const {posterHashedId, pageHashedId} = designsToPublishTo[0];
    const coverObject = designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId].cover;

    if (!coverObject?.timestamp) {
      return 0;
    }

    return coverObject.timestamp;
  };

  const isCoverUrlforUserUploadCover = (): boolean => {
    const {posterHashedId, pageHashedId} = designsToPublishTo[0];
    const coverObject = designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId].cover;

    return !!coverObject && coverObject.timestamp === null;
  };

  const getUrlForUserUploadCover = (): string | undefined => {
    if (!isCoverUrlforUserUploadCover()) {
      return undefined;
    }

    const {posterHashedId, pageHashedId} = designsToPublishTo[0];
    const coverObject = designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId].cover;

    if (!coverObject?.coverUrl) {
      return undefined;
    }

    return coverObject.coverUrl;
  };

  const handleCoverGenerationError = (source: CoverSource): void => {
    switch (source) {
      case CoverSource.DESIGN:
        dispatch(setIsLoading(false));
        dispatch(setShowCoverGenerationError(true));
        break;

      case CoverSource.UPLOAD:
        dispatch(setShowLoading(false));
        dispatch(
          setPanelMessage({
            type: PANEL_MESSAGE_TYPE.ERROR,
            message: window.i18next.t('pmwjs_unable_to_generate_cover'),
          })
        );
        break;

      default:
        console.error('Unknown cover source');
    }
  };

  const handleCoverGenerationRequest = async (socialPostCoverParams: SocialPostCoverParams, modalId: string): Promise<void> => {
    try {
      const cover = await generateCoverForPost(socialPostCoverParams);
      dispatch(closeModal(modalId));

      dispatch(
        updatePublishingParamSocialPostCover({
          design: designsToPublishTo[0],
          idAccount: account.id,
          coverObject: cover,
          platformMediaType,
        })
      );
    } catch (error) {
      console.error('Error during cover generation:', error);
      handleCoverGenerationError(socialPostCoverParams.source);
    }
  };

  const onDoneClickForUserUploadedCoverSelected = (): void => {
    dispatch(closeModal(MODALS_IDS.SOCIAL_MEDIA_SELECT_COVER_MODAL));
    const currentUploadCoverUrl = getUrlForUserUploadCover();

    if (!currentUploadCoverUrl) {
      return;
    }

    const cover: SocialPostCover = {
      timestamp: null,
      coverUrl: getCacheBustedURL(currentUploadCoverUrl),
    };

    dispatch(
      updatePublishingParamSocialPostCover({
        design: designsToPublishTo[0],
        idAccount: account.id,
        coverObject: cover,
        platformMediaType,
      })
    );
  };

  const onDoneClickForTimestampCoverSelected = (coverTimestamp: number): void => {
    if (!idPost || designsToPublishTo.length === 0) {
      window.PMW.openErrorModal({message: window.i18next.t('pmwjs_error_message')});
      return;
    }

    const {posterHashedId, pageHashedId} = designsToPublishTo[0];
    const socialPostCoverParams: SocialPostCoverParams = {
      source: CoverSource.DESIGN,
      timestampedCoverParams: {
        graphicUrl: designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId].graphicURL,
        timestamp: coverTimestamp,
        design: designsToPublishTo[0],
        idPost,
        idAccount: account.id,
      },
    };

    dispatch(setIsLoading(true));
    void handleCoverGenerationRequest(socialPostCoverParams, MODALS_IDS.SOCIAL_MEDIA_SELECT_COVER_MODAL);
  };

  const onSelectCoverPanelDoneClick = (currentCoverTimestamp: number, isUserUploadedCoverSelected: boolean): void => {
    if (isUserUploadedCoverSelected) {
      onDoneClickForUserUploadedCoverSelected();
      return;
    }

    onDoneClickForTimestampCoverSelected(currentCoverTimestamp);
  };

  const onUploadImageCrop = (hashedFileName: string, fileExtension: string, cropDataResponse: CropperPanelApplyResponse): void => {
    if (!idPost) {
      window.PMW.openErrorModal({message: window.i18next.t('pmwjs_error_message')});
      return;
    }

    const cropDataForParams: CropData = {
      x: cropDataResponse.cropData.x,
      y: cropDataResponse.cropData.y,
      width: cropDataResponse.cropData.width,
      height: cropDataResponse.cropData.height,
      imageWidth: cropDataResponse.cropData.imageWidth,
      imageHeight: cropDataResponse.cropData.imageHeight,
    };

    const socialPostCoverParams: SocialPostCoverParams = {
      source: CoverSource.UPLOAD,
      uploadedCoverPostParams: {
        uploadImageHashedFileName: hashedFileName,
        uploadImageFileExtension: fileExtension,
        cropData: cropDataForParams,
      },
    };

    dispatch(setShowLoading(true));
    void handleCoverGenerationRequest(socialPostCoverParams, MODALS_IDS.CROPPER_MODAL);
  };

  const onClickCover = (): void => {
    if (designsToPublishTo.length !== 1) {
      return;
    }

    const timestamp = getTimestampForPostCover();
    const uploadCoverUrl = getUrlForUserUploadCover();
    const {posterHashedId, pageHashedId} = designsToPublishTo[0];

    const selectCoverModalProps: SocialMediaSelectCoverModalProps = {
      videoUrl: designsGraphicsMetaMapForAccount[posterHashedId][pageHashedId].graphicURL,
      previewAspectRatio: socialAccountPostCoverValidations[account.type].previewAspectRatio,
      onDone: onSelectCoverPanelDoneClick,
      onUploadImageCrop,
      coverTimestamp: timestamp,
      showUploadButton: socialAccountPostCoverValidations[account.type].isUploadAllowed,
      showCommonCoverCheckBox: shouldShowCommonCoverCheckBoxForCoverPanel(),
      account,
      uploadCoverUrl,
    };

    dispatch(setShowCoverGenerationError(false));
    openSelectCoverModal(selectCoverModalProps);
  };

  return {
    onClickCover,
    getCoverUrlForSingleGraphicPost,
    isCoverCta,
    getTextForCoverCta,
  };
}
